// WALLET-P1 (docs/design/2026-10-27-wallet-pricing.md §3, §4): row contracts and jsonb shapes of
// the prepaid AI credits wallet. Same conventions as `rows.ts`: keys are column names,
// nullability is the column's, enums are the CHECK's; bound to their tables in
// `contract-bindings.ts`. Money is integer micros of USD everywhere (1 USD = 1,000,000 micros).
// The client contracts never carry `cost_micros` (our real cost) or a model name.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  Currency,
  TariffScope,
  WalletCheckoutStatus,
  WalletEntryKind,
  WalletRefundIntentState,
} from "./enums";
import { IsoDate, MicrosAmount, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;

// jsonb shapes ----------------------------------------------------------------------------------

/**
 * What an entry counted: a usage entry's billable units (tokens in — cached input included —,
 * tokens out, images, a reported service's cost and markup), a bonus entry's tier.
 */
export const WalletUnits = z
  .strictObject({
    tokens_in: z.int().min(0).optional(),
    tokens_out: z.int().min(0).optional(),
    /** Of `tokens_in`: cache reads, charged at the full input rate (design §3.2). */
    cache_read: z.int().min(0).optional(),
    images: z.int().min(0).optional(),
    /** HTVA-RUN-FIXES: the model a per-model client price (or the cost markup) priced. */
    model: z.string().max(128).optional(),
    /** A reported service (`report_cost`): what the agent reported, and the markup applied. */
    reported_micros: z.int().min(0).optional(),
    markup_bp: z.int().min(0).optional(),
    /**
     * A usage entry's spend category (the client's "by category" view): `tokens`, `images`, a
     * tariff unit (`video_minute`, `audio_minute`, `tts_1k_chars`, `web_search`, `web_fetch`, or an operator's
     * own), or a reported service's category (`service` when it named none).
     */
    category: z.string().max(40).optional(),
    /** A unit-priced entry: the unit and how many of it (minutes, thousands of characters, …). */
    unit: z.string().max(40).optional(),
    quantity: z.number().min(0).optional(),
    unit_price_micros: z.int().min(0).optional(),
    /** A bonus entry: the ladder row applied. */
    tier_label: z.string().max(60).nullable().optional(),
    bonus_bp: z.int().min(0).max(10_000).optional(),
  })
  .meta({ id: "WalletUnits" });
export type WalletUnits = z.infer<typeof WalletUnits>;

/** The `cost_event` a usage (or its reversal) charges for: its partition key and id. */
export const WalletCostEventRef = z
  .strictObject({ occurred_at: Timestamp, id: Uuid })
  .meta({ id: "WalletCostEventRef" });
export type WalletCostEventRef = z.infer<typeof WalletCostEventRef>;

/** Where money in came from (Stripe test mode in phase 1). Ids only, never card data. */
export const WalletPaymentRef = z
  .strictObject({
    provider: z.literal("stripe"),
    checkout_session: z.string().max(255).optional(),
    payment_intent: z.string().max(255).nullable().optional(),
    refund: z.string().max(255).optional(),
    receipt_url: z.url().max(2000).nullable().optional(),
  })
  .meta({ id: "WalletPaymentRef" });
export type WalletPaymentRef = z.infer<typeof WalletPaymentRef>;

/** Phase 2 (auto-reload); the column exists, phase 1 never writes it. */
export const WalletAutoReload = z
  .strictObject({
    below_micros: MicrosAmount,
    add_micros: MicrosAmount,
    payment_method: z.string().max(255).nullable(),
  })
  .meta({ id: "WalletAutoReload" });
export type WalletAutoReload = z.infer<typeof WalletAutoReload>;

// Rows ------------------------------------------------------------------------------------------

/**
 * One wallet ledger line (`cell.wallet_entry`, append-only). Signed: money in is positive, usage
 * and refunds negative; a reversal has the opposite sign of the entry it reverses.
 * `balance_after_micros` is the company balance right after this entry (written with it).
 */
export const WalletEntry = z
  .object({
    company_id: Uuid,
    occurred_at: Timestamp,
    id: Uuid,
    kind: WalletEntryKind,
    amount_micros: MicrosAmount,
    currency: Currency,
    meter_identity: z.string(),
    balance_after_micros: MicrosAmount,
    cost_event_ref: WalletCostEventRef.nullable(),
    tariff_id: Uuid.nullable(),
    tariff_version: z.int().nullable(),
    units: WalletUnits.nullable(),
    run_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    membership_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    workforce_agent_id: Uuid.nullable(),
    payment_ref: WalletPaymentRef.nullable(),
    reverses_entry_id: Uuid.nullable(),
    label: z.string(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "WalletEntry" });
export type WalletEntry = z.infer<typeof WalletEntry>;

/** The operator's view of an entry: the client's plus our real cost (design §4.3). */
export const OperatorWalletEntry = WalletEntry.extend({
  cost_micros: MicrosAmount.nullable(),
}).meta({ id: "OperatorWalletEntry" });
export type OperatorWalletEntry = z.infer<typeof OperatorWalletEntry>;

/** A company's wallet switches and limits (`cell.wallet_settings`). */
export const WalletSettings = z
  .object({
    company_id: Uuid,
    enabled: z.boolean(),
    enabled_at: Timestamp.nullable(),
    min_topup_micros: MicrosAmount,
    max_topup_micros: MicrosAmount,
    low_watermark_bp: z.int(),
    auto_reload: WalletAutoReload.nullable(),
    paused_at: Timestamp.nullable(),
    low_warned_at: Timestamp.nullable(),
    ...actor,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "WalletSettings" });
export type WalletSettings = z.infer<typeof WalletSettings>;

/** A unit or category key: lowercase, digits and `_` (`video_minute`, `stock_photo`). */
export const TariffUnitKey = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9_]{1,39}$/, "lowercase letters, digits and _ (2–40)");

/**
 * One per-unit price line of a tariff version (design §3.1, scope additions 2026-10-27): media
 * and tool units charged per unit instead of per token. Integer micros per one `per`.
 */
export const TariffUnitPrice = z
  .strictObject({
    unit: TariffUnitKey,
    label: z.string().trim().min(1).max(60),
    /** What one unit is, as the client reads it: "minute", "1k characters", "call", … */
    per: z.string().trim().min(1).max(30),
    price_micros: z.int().min(0).max(1_000_000_000),
  })
  .meta({ id: "TariffUnitPrice" });
export type TariffUnitPrice = z.infer<typeof TariffUnitPrice>;

/** A reported-spend category's markup over cost (basis points), when it has no unit price. */
export const TariffCategoryMarkup = z
  .strictObject({ category: TariffUnitKey, markup_bp: z.int().min(0).max(100_000) })
  .meta({ id: "TariffCategoryMarkup" });
export type TariffCategoryMarkup = z.infer<typeof TariffCategoryMarkup>;

/**
 * The known units and their launch prices (Andrei, 2026-10-27): what the gateway maps media
 * calls to, and what a new tariff version starts from. Images keep their own column.
 */
export const KNOWN_TARIFF_UNITS: readonly TariffUnitPrice[] = [
  { unit: "video_minute", label: "Video analysed", per: "minute", price_micros: 100_000 },
  { unit: "audio_minute", label: "Audio transcribed", per: "minute", price_micros: 20_000 },
  { unit: "tts_1k_chars", label: "Speech generated", per: "1k characters", price_micros: 30_000 },
  // Only successful calls are billable (the gateway books a failed call at no units).
  { unit: "web_search", label: "Web search", per: "call", price_micros: 5_000 },
  { unit: "web_fetch", label: "Web page fetch", per: "call", price_micros: 5_000 },
];

/**
 * HTVA-RUN-FIXES (client price per model): an explicit client price for one model, per million
 * tokens (micros of USD). Cache reads at `cache_read_per_m_micros` when set, else at the input
 * rate. Beats the cost markup and the model-blind rates for that model.
 */
export const TariffModelPrice = z
  .strictObject({
    model: z.string().trim().min(1).max(128),
    input_per_m_micros: z.int().min(0).max(1_000_000_000_000),
    output_per_m_micros: z.int().min(0).max(1_000_000_000_000),
    cache_read_per_m_micros: z.int().min(0).max(1_000_000_000_000).nullable().optional(),
  })
  .meta({ id: "TariffModelPrice" });
export type TariffModelPrice = z.infer<typeof TariffModelPrice>;

/**
 * OPERATOR-CONSOLE: a plan's own client price. With a model: that model's price per million for
 * companies on the plan; without one: the plan's markup over our cost for every model. Before the
 * version's model prices and markup; a company's own tariff version still wins over both.
 */
export const TariffPlanPrice = z
  .strictObject({
    plan_key: z.string().trim().min(1).max(64),
    model: z.string().trim().min(1).max(128).nullable(),
    input_per_m_micros: z.int().min(0).max(1_000_000_000_000).nullable(),
    output_per_m_micros: z.int().min(0).max(1_000_000_000_000).nullable(),
    cache_read_per_m_micros: z.int().min(0).max(1_000_000_000_000).nullable().optional(),
    markup_bp: z.int().min(0).max(100_000).nullable(),
  })
  .refine(
    (p) =>
      p.model === null
        ? p.markup_bp !== null && p.input_per_m_micros === null && p.output_per_m_micros === null
        : p.input_per_m_micros !== null && p.output_per_m_micros !== null && p.markup_bp === null,
    "a plan line names a model and its two prices, or no model and a markup",
  )
  .meta({ id: "TariffPlanPrice" });
export type TariffPlanPrice = z.infer<typeof TariffPlanPrice>;

/** Model-blind rates: per million tokens, per image, per unit (design §3.1). Micros of USD. */
const rates = {
  input_per_m_micros: MicrosAmount,
  output_per_m_micros: MicrosAmount,
  image_micros: MicrosAmount,
  unit_prices: z.array(TariffUnitPrice),
  reported_cost_markup_bp: z.int(),
  category_markups: z.array(TariffCategoryMarkup),
  /** Explicit client prices per model (before the markup and the model-blind rates). */
  model_prices: z.array(TariffModelPrice),
  /**
   * The client price of a model without an explicit price: our cost (the cost price list)
   * × (1 + this markup). Null: such a model is charged at the model-blind rates.
   */
  cost_markup_bp: z.int().nullable(),
  /** OPERATOR-CONSOLE: per-plan client prices, before `model_prices` and `cost_markup_bp`. */
  plan_prices: z.array(TariffPlanPrice),
};

/** A tariff version in a cell (`cell.tariff`): the platform list's mirror or a company override. */
export const Tariff = z
  .object({
    company_id: Uuid,
    id: Uuid,
    scope: TariffScope,
    source_tariff_id: Uuid.nullable(),
    version: z.int(),
    label: z.string(),
    effective_from: IsoDate,
    ...rates,
    currency: Currency,
    retired_at: Timestamp.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "Tariff" });
export type Tariff = z.infer<typeof Tariff>;

/** The platform tariff (`cp.tariff`), written by the operator; cells read its mirror. */
export const CatalogTariff = z
  .object({
    id: Uuid,
    version: z.int(),
    label: z.string(),
    effective_from: IsoDate,
    ...rates,
    currency: Currency,
    created_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
    retired_at: Timestamp.nullable(),
  })
  .meta({ id: "CatalogTariff" });
export type CatalogTariff = z.infer<typeof CatalogTariff>;

/** One rung of the top-up bonus ladder (`cp.topup_bonus_tier`, design §3.3). */
export const TopupBonusTier = z
  .object({
    id: Uuid,
    min_amount_micros: MicrosAmount,
    bonus_bp: z.int(),
    label: z.string().nullable(),
    effective_from: IsoDate,
    created_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "TopupBonusTier" });
export type TopupBonusTier = z.infer<typeof TopupBonusTier>;

/** A Checkout Session the API created for a top-up (`cell.wallet_checkout`). */
export const WalletCheckout = z
  .object({
    company_id: Uuid,
    id: Uuid,
    session_id: z.string(),
    status: WalletCheckoutStatus,
    amount_micros: MicrosAmount,
    bonus_micros: MicrosAmount,
    bonus_bp: z.int(),
    tier_label: z.string().nullable(),
    payment_intent: z.string().nullable(),
    receipt_url: z.string().nullable(),
    topup_entry_id: Uuid.nullable(),
    refunded_micros: MicrosAmount,
    ...actor,
    created_at: Timestamp,
    completed_at: Timestamp.nullable(),
    updated_at: Timestamp,
  })
  .meta({ id: "WalletCheckout" });
export type WalletCheckout = z.infer<typeof WalletCheckout>;

/**
 * An operator's refund request (`cell.wallet_refund_intent`), keyed by its Idempotency-Key:
 * pending (holding `hold_micros` from the credit) until booked or failed at Stripe.
 */
export const WalletRefundIntent = z
  .object({
    company_id: Uuid,
    request_key: Uuid,
    checkout_id: Uuid,
    amount_micros: MicrosAmount,
    hold_micros: MicrosAmount,
    reason: z.string(),
    principal_id: Uuid.nullable(),
    state: WalletRefundIntentState,
    refund_id: z.string().nullable(),
    entry_id: Uuid.nullable(),
    entry_occurred_at: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "WalletRefundIntent" });
export type WalletRefundIntent = z.infer<typeof WalletRefundIntent>;
